// useProfile — fetch the signed-in member's profile once and expose it.
//
// Backs admin-gated UI: features that only admins may use (e.g. the Education
// page "Add Learning" action) read `isAdmin` from here. This is a UX gate only
// — the backend independently enforces admin on every admin route (403), so a
// non-admin who bypasses the hidden button still can't mutate anything.
//
// First login needs no extra input: the portal is NBS-only, so the backend
// creates the member profile with the NBS division automatically.

import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/lib/api-types";

// Generated from the OpenAPI schema — now includes the composed `avatar`
// object (see api/schemas/members.py MemberProfileResponse.avatar).
export type MemberProfile = components["schemas"]["MemberProfileResponse"];

export function useProfile(): {
  profile: MemberProfile | null;
  isAdmin: boolean;
  loading: boolean;
  /** Replace the cached profile (e.g. after the user saves a new avatar) so
   *  the top-bar avatar updates instantly without a full reload. */
  setProfile: (profile: MemberProfile) => void;
} {
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient<MemberProfile>("/api/v1/members/me/profile")
      .then((p) => !cancelled && setProfile(p))
      .catch(() => !cancelled && setProfile(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const updateProfile = useCallback((next: MemberProfile) => setProfile(next), []);

  return { profile, isAdmin: profile?.is_admin ?? false, loading, setProfile: updateProfile };
}
